import { Node, NodeProps } from "@xyflow/react";
import { Suspense } from "react";
import { useAtomValue } from "jotai";
import classNames from "classnames";
import { SkeletonText } from "@zapier/design-system";
import {
  isMultiSelectedAtomFamily,
  isOnlyNodeSelectedAtomFamily,
  DefaultNodeData,
  shouldShowStepIdsAtom,
  shouldShowAddStepButtonAtom,
  isViewOnlyModeAtom,
  aiSuggestedEditsAtomFamily,
  aiSuggestedEditsLoadingAtomFamily,
  areHandlesVisibleAtomFamily,
  NodeType,
  isValidUUID,
  assetAccessAtomFamily_SUSPENDS,
  determineAssetType,
  hasSourceNodeAtomFamily,
  hasVisibleAnnotationAtomFamily,
  nodeIdToExplainAtom,
  getAssetId,
  isDraftNotTemplateNodeAtomFamily,
} from "@zapier/canvas-state";
import { copilotLoadingNodeIdAtom } from "@zapier/copilot-tools";
import { isRemirrorJSONEmpty } from "@zapier/rich-text-editor";

import { AddStepButton } from "../../../../components/AddStepButton";
import { SmartFlyout } from "../../../SmartFlyout/SmartFlyout";
import { StepId } from "../../../StepId";
import { getDefaultColorForAssetType } from "../../../../utils/getDefaultColorForAssetType";
import { getDefaultBackgroundColorForAssetType } from "../../../../utils/getDefaultBackgroundColorForAssetType";
import { NodeHoverWrapper } from "../../../NodeHoverWrapper";
import { FloatingInlineToolbar } from "../../../FloatingInlineToolbar";
import { Handles } from "../../../Handles";
import { CanvasCopilotIndicator } from "../../../CanvasCopilotIndicator";
import { AssetToggleOrStatus } from "../../../AssetToggleOrStatus";

import { DefaultNodeContent } from "./DefaultNodeContent";
import styles from "./DefaultNode.module.css";

function DefaultNodeInner(props: NodeProps<Node<DefaultNodeData>>) {
  const { id, data, type } = props;
  const assetType = determineAssetType(data.services, type as NodeType);
  const assetId = getAssetId(data, assetType);
  const isAssetUnlinked = Boolean(assetType && !assetId);
  const isDraftNotTemplateNode = useAtomValue(
    isDraftNotTemplateNodeAtomFamily(id),
  );

  const backgroundColor = getDefaultBackgroundColorForAssetType({
    assetType,
    assetId,
    color: data.color, // we need actual color without fallback from getDefaultColorForAssetType
    isDraftNotTemplateNode,
  });
  const {
    color = getDefaultColorForAssetType({
      assetType,
      assetId,
      isDraftNotTemplateNode,
    }),
  } = data;

  const hasSourceNode = useAtomValue(hasSourceNodeAtomFamily(id));
  const shouldShowStepIds = useAtomValue(shouldShowStepIdsAtom);
  const isViewOnlyMode = useAtomValue(isViewOnlyModeAtom);
  const isMultiSelected = useAtomValue(isMultiSelectedAtomFamily(id));
  const shouldShowAddStepButton = useAtomValue(shouldShowAddStepButtonAtom);
  const aiSuggestedEdits = useAtomValue(aiSuggestedEditsAtomFamily(id));
  const aiSuggestedEditsLoading = useAtomValue(
    aiSuggestedEditsLoadingAtomFamily(id),
  );
  const isSelected = useAtomValue(isOnlyNodeSelectedAtomFamily(id));
  const areHandlesVisible = useAtomValue(areHandlesVisibleAtomFamily(id));

  const { isAssetForbidden, isAssetInaccessible } = useAtomValue(
    assetAccessAtomFamily_SUSPENDS({
      assetId,
      interfaceProjectId: data.projectId,
      assetType,
    }),
  );
  const nodeIdToExplain = useAtomValue(nodeIdToExplainAtom);
  const isBeingExplained = nodeIdToExplain === id;

  const isToolbarVisible =
    !isAssetInaccessible &&
    !isViewOnlyMode &&
    isSelected &&
    isValidUUID(id) &&
    !isBeingExplained;
  const hasNotes = !isRemirrorJSONEmpty(data.notesJson);
  const hasVisibleAnnotation = useAtomValue(hasVisibleAnnotationAtomFamily(id));
  const loadingNodeId = useAtomValue(copilotLoadingNodeIdAtom);

  const shouldShowCopilotIndicator = loadingNodeId === id;

  return (
    <>
      {shouldShowCopilotIndicator ? (
        <CanvasCopilotIndicator nodeId={id} />
      ) : (
        <AssetToggleOrStatus assetType={assetType} assetId={assetId} />
      )}

      {shouldShowStepIds && <StepId />}
      {!hasSourceNode && shouldShowAddStepButton && (
        <AddStepButton handleType="target" />
      )}
      {shouldShowAddStepButton && <AddStepButton handleType="source" />}
      <NodeHoverWrapper>
        {isToolbarVisible && (
          <FloatingInlineToolbar
            color={color}
            selectedApps={data.services}
            actors={data.actors}
            hasNotes={hasNotes}
            link={data.link}
            displayLinkAs={data.displayLinkAs}
            nodeType={type as NodeType}
            assetIds={{
              tableId: data.tableId,
              interfaceProjectId: data.projectId,
              interfacePageId: data.pageId,
              chatbotId: data.chatbotId,
              agentId: data.agentId,
            }}
            isAssetUnlinked={isAssetUnlinked}
          />
        )}
        <DefaultNodeContent
          {...props}
          data={{ ...data, color }}
          className={classNames({
            [styles.wrapperMultiSelected]: isMultiSelected,
            [styles.wrapperSingleSelected]: isSelected || hasVisibleAnnotation,
          })}
          isViewOnlyMode={isViewOnlyMode || isAssetForbidden}
          backgroundColor={backgroundColor}
        />

        <Handles nodeType="default" isVisible={areHandlesVisible} />
        {((aiSuggestedEdits?.additions &&
          aiSuggestedEdits.additions.length > 0) ||
          aiSuggestedEditsLoading) && <SmartFlyout nodeId={id} />}
      </NodeHoverWrapper>
    </>
  );
}

export function DefaultNode(props: NodeProps<Node<DefaultNodeData>>) {
  return (
    <Suspense fallback={<LoadingNode />}>
      <DefaultNodeInner {...props} />
    </Suspense>
  );
}

/* IMPORTANT! We need Handles in the loading state or else the edges won't get rendered even though they exist */
function LoadingNode() {
  return (
    <div className={styles.wrapper}>
      <Handles nodeType="default" />
      <SkeletonText lines={2} size="body" textAlign="left" width="100%" />
    </div>
  );
}
